import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import {
  useGetCountries,
  useGetInformationByCode,
} from '../../../app/pages/account/hooks/AccountHook';
import { ColoniaSelectModel } from '../models/AddressModel';
import { KTSVG } from './KTSVG';
import { Modal } from 'react-bootstrap-v5';
import * as Yup from 'yup';
import Select from 'react-select';
import { NumericFormat } from 'react-number-format';
import { styleSelect } from '../SelectStyles';
import { SimpleAlert } from '../../../_metronic/helpers';

type Props = {
  show: boolean;
  isEditable: boolean;
  dataEditable: any;
  handleSubmit: (values: any) => void;
  handleClose: () => void;
};

const AddressModal: React.FC<Props> = ({
  show,
  handleClose,
  isEditable,
  dataEditable,
  handleSubmit,
}) => {
  const [loading, setLoading] = useState(false);
  const [initialValues, setInitialValues] = useState(dataEditable);
  const [showBtn, setShowBtn] = useState<boolean>(false);

  // SELECT DATA
  const [colonias, setColonias] = useState<ColoniaSelectModel[]>([]);
  const [country, setCountry] = useState<any | null | ''>('');
  // VALUE SELECT
  const [colonia, setColonia] = useState<any | null | ''>('');
  // VALUE INPUT
  const [inputColonia, setInputColonia] = useState<string>('');
  const [postalCode, setPostalCode] = useState<number>(0);
  const [disabledInput, setDisabledInput] = useState<boolean>(false);
  const { dataLocation, loadingPostalCodeFetch } =
    useGetInformationByCode(postalCode);
  const [zipCodeChange, setZipCodeChange] = useState(false);
  const { dataCountry } = useGetCountries();
  const [showColoniaInput, setShowColoniaInput] = useState(false);

  const AddressSchema = Yup.object().shape({
    nombre: Yup.string().required('Este campo es requerido.').max(70, 'Máximo 70 caracteres.'),
    calle: Yup.string().required('Este campo es requerido.').max(50, 'Máximo 50 caracteres.'),
    numero_exterior: Yup.number()
      .min(1, 'Este campo es requerido.')
      .required('Este campo es requerido.')
      .typeError('Debe ingresar un número.')
      .test(
        'len',
        'Máximo 10 caracteres.',
        (val) => val ? val.toString().length <= 10 : true
      ),
    numero_interior: Yup.number()
      .typeError('Debe ser un número')
      .integer('Debe ser un número entero')
      .positive('Debe ser positivo')
      .test(
        'len',
        'Máximo 10 caracteres.',
        (val) => val ? val.toString().length <= 10 : true
      ),
    codigo_postal: Yup.number()
      .min(1, 'Este campo es requerido.')
      .required('Este campo es requerido.')
      .typeError('Debe ingresar un número.')
      .test(
        'len',
        'Máximo 10 caracteres.',
        (val) => val ? val.toString().length <= 10 : true
      ),
    ciudad: Yup.string()
      .max(50, 'Máximo 50 caracteres.'),
    colonia: Yup.string()
      .max(50, 'Máximo 50 caracteres.')
      .nullable(),
    estado: Yup.string()
      .max(50, 'Máximo 50 caracteres.'),
  });

  // console.log(isEditable);
  // console.log(dataEditable);

  const formAddress = useFormik({
    initialValues: initialValues,
    validationSchema: AddressSchema,
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    onSubmit: (values, { setSubmitting }) => {
      setLoading(true);
      setTimeout(() => {
        handleSubmit(values);
        setLoading(false);
        console.log("first")
      }, 1000);
    },
  });

  const handleSelect = (selectedOption: any) => {
    const { value } = selectedOption;
    setCountry(selectedOption);
    formAddress.setFieldValue('pais', value);
  };

  const handleSelectColonia = (selectedOption: any) => {
    const { value } = selectedOption;
    if (value !== 'Otra') {
      setColonia(selectedOption);
      setShowColoniaInput(false);
      formAddress.setFieldValue('otro', false);
      formAddress.setFieldValue('colonia', value);
    } else {
      setShowBtn(true);
      setColonia(null);
      setShowColoniaInput(true);
      formAddress.setFieldValue('otro', true);
      formAddress.setFieldValue('colonia', null);
      setInputColonia('');
    }
  };

  const handleCleanFormAtt = () => {
    formAddress.setFieldValue('colonia', '');
    formAddress.setFieldValue('estado', '');
    formAddress.setFieldValue('ciudad', '');
  };

  const handleCleanData = () => {
    setDisabledInput(false);
    setShowBtn(false);
    setColonia('');
    setInputColonia('');
    setColonias([]);
    setPostalCode(0);
    setInputColonia('');
    setCountry('');
  };

  useEffect(() => {
    if (isEditable && dataEditable) {
      const {
        id,
        nombre,
        codigo_postal,
        pais = '',
        calle,
        numero_exterior,
        numero_interior,
        colonia = '',
        estado = '',
        ciudad = '',
        otro = false
      } = dataEditable;

      const countryObj = pais ? { value: pais.trim(), label: pais.trim() } : null;
      setCountry(countryObj);

      const initialFormValues = {
        id,
        nombre,
        calle,
        numero_exterior,
        numero_interior,
        codigo_postal,
        pais,
        colonia,
        estado,
        ciudad,
        otro
      };

      setInitialValues(initialFormValues);
      setPostalCode(parseInt(codigo_postal) || 0);
      setInputColonia(colonia);

      if (pais.trim() === 'México') {
        if (otro) {
          setShowColoniaInput(true);
          setShowBtn(true);
        } else {
          setColonia(colonia ? { value: colonia, label: colonia } : null);
        }
      }
    } else {
      const resetValues = {
        id: 0,
        nombre: '',
        calle: '',
        numero_exterior: '',
        numero_interior: '',
        codigo_postal: '',
        pais: '',
        colonia: '',
        estado: '',
        ciudad: '',
        otro: false
      };
      setInitialValues(resetValues);
      setPostalCode(0);
      setCountry(null);
      setColonia(null);
      setInputColonia('');
      setShowColoniaInput(false);
    }
  }, [show, isEditable, dataEditable?.id]);

  useEffect(() => {
    if (!show) {
      setInitialValues({
        id: 0,
        nombre: '',
        calle: '',
        numero_exterior: '',
        numero_interior: '',
        codigo_postal: '',
        pais: '',
        colonia: '',
        estado: '',
        ciudad: '',
        otro: false
      });
      setDisabledInput(false);
      setPostalCode(0);
      setColonia(null);
      setShowColoniaInput(false);
      setCountry(null);
      setInputColonia('');
      setColonias([]);
      setShowBtn(false);
    }
  }, [show]);

  useEffect(() => {
    if (country?.value !== 'México') {
      setDisabledInput(false);
      return;
    }

    if (dataLocation?.codigo_postal) {
      const { estado, municipio } = dataLocation.codigo_postal;

      if (formAddress.values.estado !== estado) {
        formAddress.setFieldValue('estado', estado);
      }
      if (formAddress.values.ciudad !== municipio) {
        formAddress.setFieldValue('ciudad', municipio);
      }
      setDisabledInput(true);
    }

    if (dataLocation) {
      const { estado, municipio, colonias } = dataLocation.codigo_postal;

      if (formAddress.values.estado !== estado) {
        formAddress.setFieldValue('estado', estado);
      }
      if (formAddress.values.ciudad !== municipio) {
        formAddress.setFieldValue('ciudad', municipio);
      }
      setDisabledInput(true);

      if (colonias?.length) {
        const selectOptions: ColoniaSelectModel[] = [
          ...colonias.map((colonia: string) => ({
            value: colonia,
            label: colonia
          })),
          { value: 'Otra', label: 'Otra' }
        ];
        setColonias(selectOptions);

        if (isEditable) {
          const validate_colonia = colonias.some(item => item === initialValues.colonia);
          if (validate_colonia) {
            setColonia({
              value: initialValues.colonia,
              label: initialValues.colonia
            });
            formAddress.setFieldValue('colonia', initialValues.colonia);
            setShowColoniaInput(false);
          } else if (initialValues.otro) {
            setInputColonia(initialValues.colonia);
            setShowColoniaInput(true);
            setShowBtn(true);
          }
        }
      }
    } else {
      if (zipCodeChange && !loadingPostalCodeFetch) {
        showLocationWarning();
        handleCleanFormAtt();
      }
    }
  }, [dataLocation, country?.value]);

  useEffect(() => {
    if (zipCodeChange && !loadingPostalCodeFetch && !dataLocation && country?.value === 'México') {
      showLocationWarning();
      setInputColonia('');
      setColonia(null);
      setZipCodeChange(false);
      setColonias([]);
      setShowBtn(false);
      setDisabledInput(false);
      handleCleanFormAtt();
    }
  }, [loadingPostalCodeFetch, country?.value]);

  useEffect(() => {
    if (!country) return;

    if (country.value !== 'México') {
      setDisabledInput(false);
      if (formAddress.values.estado === dataLocation?.codigo_postal?.estado) {
        formAddress.setFieldValue('estado', '');
      }
      if (formAddress.values.ciudad === dataLocation?.codigo_postal?.municipio) {
        formAddress.setFieldValue('ciudad', '');
      }
    } else {
      setDisabledInput(true);
    }
  }, [country]);

  useEffect(() => {
    if (country && country.value !== 'México') {
      setDisabledInput(false);
      formAddress.setFieldValue('colonia', inputColonia || '');
    }
  }, [country]);

  useEffect(() => {
    if (!show) {
      if (!isEditable) {
        setInitialValues({});
        setDisabledInput(false);
        setPostalCode(0);
        setColonia(null);
        setShowColoniaInput(false);
      }
    }
  }, [show]);

  useEffect(() => {
    if (!isEditable || !dataEditable) return;

    const timer = setTimeout(() => {
      if (!formAddress.values.estado && dataEditable.estado) {
        formAddress.setFieldValue('estado', dataEditable.estado);
      }
      if (!formAddress.values.ciudad && dataEditable.ciudad) {
        formAddress.setFieldValue('ciudad', dataEditable.ciudad);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [isEditable, dataEditable?.id]);

  const showLocationWarning = () => {
    if (!dataLocation && !loadingPostalCodeFetch) {
      SimpleAlert(
        'No existe información para ese Código Postal',
        3000,
        'warning'
      );
    }
  };

  return (
    <Modal
      id="kt_modal_create"
      tabIndex={-1}
      aria-hidden="true"
      backdrop="static"
      dialogClassName="modal-dialog mw-800px"
      show={show}
      onHide={() => {
        formAddress.resetForm();
        handleClose();
      }}
    >
      <div className="modal-content">
        <div className="modal-header">
          <h2 className="text-muted">
            {formAddress.values.id <= 0 ? 'Nueva' : 'Editar'} dirección
          </h2>
          <div
            className="btn btn-sm btn-icon btn-active-color-primary"
            onClick={() => {
              formAddress.resetForm();
              handleClose();
              handleCleanData();
            }}
          >
            <KTSVG
              className="svg-icon-2"
              path="/media/icons/duotune/arrows/arr061.svg"
            />
          </div>
        </div>
        <div className="modal-body mx-5 padding-top-00">
          <form
            id="kt_modal_generic_form"
            className="form"
            action="#"
            onSubmit={formAddress.handleSubmit}
          >
            <div className="d-flex flex-column flex-xl-row flex-row-fluid mt-5">
              <div className="w-100 d-flex">
                <div className="w-100 pe-5">
                  <div>
                    <div className="row g-9 mb-7">
                      <div className="col-md-12 fv-row fv-plugins-icon-container">
                        <input
                          className="form-control mb-2 w-100 border-0"
                          style={{
                            width: '100%',
                            padding: '15px',
                            fontSize: '24px',
                            boxSizing: 'border-box',
                            marginBottom: '20px',
                          }}
                          data-kt-element="input"
                          placeholder="Nombre *"
                          {...formAddress.getFieldProps('nombre')}
                        ></input>
                        {formAddress.touched.nombre &&
                          formAddress.errors.nombre && (
                            <div className="fv-plugins-message-container text-danger w-100">
                              <span role="alert">
                                {formAddress.errors.nombre}
                              </span>
                            </div>
                          )}
                      </div>
                    </div>
                    <div className="row g-9 mb-7">
                      {/* PAIS */}
                      <div className="col-md-4 fv-row fv-plugins-icon-container">
                        <label className="fs-6 fw-bold mb-2">País</label>
                        <Select
                          options={dataCountry}
                          styles={styleSelect}
                          placeholder={'Seleccione'}
                          isSearchable={true}
                          noOptionsMessage={() => {
                            return <span>{'No se encontraron registros'}</span>;
                          }}
                          onChange={handleSelect}
                          value={country}
                        />
                      </div>
                      {/* CALLE */}
                      <div className="col-md-4 fv-row fv-plugins-icon-container">
                        <label className="fs-6 fw-bold mb-2 required">
                          Calle
                        </label>
                        <input
                          {...formAddress.getFieldProps('calle')}
                          className="form-control"
                          name="calle"
                        />
                        {formAddress.touched.calle && formAddress.errors.calle && (
                          <div className="fv-plugins-message-container text-danger w-100">
                            <span role="alert">{formAddress.errors.calle}</span>
                          </div>
                        )}
                      </div>
                      {/* NO. EXTERIOR */}
                      <div className="col-md-4 fv-row fv-plugins-icon-container">
                        <label className="fs-6 fw-bold mb-2 required">
                          Número exterior
                        </label>
                        <NumericFormat
                          className="form-control"
                          {...formAddress.getFieldProps('numero_exterior')}
                          onBlur={(event) => { }}
                        />
                        {formAddress.touched.numero_exterior &&
                          formAddress.errors.numero_exterior && (
                            <div className="fv-plugins-message-container text-danger w-100">
                              <span role="alert">
                                {formAddress.errors.numero_exterior}
                              </span>
                            </div>
                          )}
                      </div>
                      {/* NO. INTERIOR */}
                      <div className="col-md-4 fv-row fv-plugins-icon-container">
                        <label className=" fs-6 fw-bold mb-2">
                          Número interior
                        </label>
                        <NumericFormat
                          className="form-control"
                          {...formAddress.getFieldProps('numero_interior')}
                          onBlur={(event) => { }}
                        />
                        {formAddress.touched.numero_interior &&
                          formAddress.errors.numero_interior && (
                            <div className="fv-plugins-message-container text-danger w-100">
                              <span role="alert">
                                {formAddress.errors.numero_interior}
                              </span>
                            </div>
                          )}
                      </div>
                      {/*CODIGO POSTAL*/}
                      <div className="col-md-4 fv-row fv-plugins-icon-container">
                        <label className=" fs-6 fw-bold mb-2 required">
                          Código Postal
                        </label>
                        <NumericFormat
                          className="form-control"
                          {...formAddress.getFieldProps('codigo_postal')}
                          onBlur={(event) => {
                            if (event.target.value) {
                              setPostalCode(parseInt(event.target.value));
                              formAddress.setFieldValue('colonia', '');
                              setZipCodeChange(true);
                            } else {
                              setPostalCode(0);
                              setColonia(null);
                              setInputColonia('');
                              setDisabledInput(false);
                              formAddress.setFieldValue('estado', '');
                              formAddress.setFieldValue('ciudad', '');
                            }
                          }}
                          disabled={loadingPostalCodeFetch}
                        />
                        {formAddress.touched.codigo_postal &&
                          formAddress.errors.codigo_postal && (
                            <div className="fv-plugins-message-container text-danger w-100">
                              <span role="alert">
                                {formAddress.errors.codigo_postal}
                              </span>
                            </div>
                          )}
                      </div>
                      {/*COLONIA*/}
                      <div className="col-md-4 fv-row fv-plugins-icon-container">
                        <div className="w-100 d-flex">
                          <label className="fw-bold fs-6 mb-2 w-auto">
                            Colonia
                          </label>
                        </div>
                        {country?.value === 'México' ? (
                          !showColoniaInput ? (
                            <Select
                              options={colonias}
                              styles={styleSelect}
                              placeholder={'Seleccione'}
                              isLoading={loadingPostalCodeFetch}
                              isSearchable={true}
                              noOptionsMessage={() => <span>{'No se encontraron registros'}</span>}
                              onChange={handleSelectColonia}
                              value={colonia}
                              isDisabled={loadingPostalCodeFetch}
                            />
                          ) : (
                            <>
                              <input
                                {...formAddress.getFieldProps('colonia')}
                                className="form-control"
                                value={inputColonia}
                                name="colonia"
                                onChange={(event) => {
                                  setInputColonia(event.target.value);
                                  formAddress.setFieldValue('colonia', event.target.value);
                                }}
                              />
                              {formAddress.touched.colonia && formAddress.errors.colonia && (
                                <div className="fv-plugins-message-container text-danger w-100">
                                  <span role="alert">{formAddress.errors.colonia}</span>
                                </div>
                              )}
                              {colonias.length > 0 && (
                                <a
                                  className="cursor-pointer btn-ligth-primary mt-2"
                                  onClick={() => {
                                    formAddress.setFieldValue('colonia', '');
                                    setColonia(null);
                                    setShowColoniaInput(false);
                                  }}
                                >
                                  Ver listado
                                </a>
                              )}
                            </>
                          )
                        ) : (
                          <>
                            <input
                              {...formAddress.getFieldProps('colonia')}
                              className="form-control"
                              name="colonia"
                              onChange={(event) => {
                                formAddress.setFieldValue('colonia', event.target.value);
                              }}
                            />
                            {formAddress.touched.colonia && formAddress.errors.colonia && (
                              <div className="fv-plugins-message-container text-danger w-100">
                                <span role="alert">{formAddress.errors.colonia}</span>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                      {/* CIUDAD */}
                      <div className="col-md-4 fv-row fv-plugins-icon-container">
                        <label className="fw-bold fs-6 mb-2 w-auto">
                          Ciudad
                        </label>
                        <input
                          className="form-control"
                          data-kt-element="input"
                          name="ciudad"
                          value={formAddress.values.ciudad || ''}
                          onChange={formAddress.handleChange}
                          disabled={country?.value === 'México' && disabledInput}
                        ></input>
                        {formAddress.touched.ciudad &&
                          formAddress.errors.ciudad && (
                            <div className="fv-plugins-message-container text-danger w-100">
                              <span role="alert">
                                {formAddress.errors.ciudad}
                              </span>
                            </div>
                          )}
                      </div>
                      {/* ESTADO */}
                      <div className="col-md-4 fv-row fv-plugins-icon-container">
                        <label className="fs-6 fw-bold mb-2">Estado</label>
                        <input
                          className="form-control"
                          data-kt-element="input"
                          name="estado"
                          value={formAddress.values.estado || ''}
                          onChange={formAddress.handleChange}
                          disabled={country?.value === 'México' && disabledInput}
                        ></input>
                        {formAddress.touched.estado &&
                          formAddress.errors.estado && (
                            <div className="fv-plugins-message-container text-danger w-100">
                              <span role="alert">
                                {formAddress.errors.estado}
                              </span>
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-end mt-10">
              <button
                type="reset"
                className="btn btn-light me-3"
                data-kt-users-modal-action="cancel"
                onClick={() => {
                  formAddress.resetForm();
                  handleClose();
                  handleCleanData();
                }}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                data-kt-users-modal-action="submit"
                disabled={loading}
              >
                {!loading && <span className="indicator-label">Guardar</span>}
                {loading && (
                  <span
                    className="indicator-progress"
                    style={{ display: 'block' }}
                  >
                    Espere por favor...
                    <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                  </span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};
export { AddressModal };
